// src/components/PremiumLock.jsx — Premium Feature Lock System v275 (Item 7)
// ═════════════════════════════════════════════════════════════════
// WHAT'S HERE (post-Item 7 cleanup):
//   PaywallModal    — full conversion paywall w/ FOMO + plans + bonus
//   UpgradeNudge    — inline contextual nudge strip (used by 3 pages)
//
// REMOVED IN v275 (Item 7 — Rule L Phase 2 verified zero consumers):
//   PremiumLock     — default export, ZERO consumers project-wide
//   BlurPreview     — imported by PnLPage but never JSX-rendered (eslint-disabled)
//   LimitProgress   — ZERO consumers
//   UpgradeModal    — ZERO consumers (legacy compat layer over PaywallModal)
//   LimitBadge      — ZERO consumers (legacy compat layer over LimitProgress)
//   usePremium re-export (line 26) — backward-compat stub never triggered;
//     all consumers import from canonical webapp/src/hooks/usePremium.js
// ═════════════════════════════════════════════════════════════════
//
// v275 (G4.23a): usePremium hook moved to canonical location at
// webapp/src/hooks/usePremium.js. The re-export from this file was deleted
// in Item 7 after Rule L Phase 2 verification confirmed zero external imports.
// All surviving components below use the canonical hook.
import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { usePlans, planPriceText, portfolioText } from '../hooks/usePlans';   // S-161
import { G, GD } from '../constants';
import { usePremium } from '../hooks/usePremium';

// ─────────────────────────────────────────────────────────────────
// PremiumLock + BlurPreview REMOVED in v275 (Item 7)
// Both were dead code (zero JSX usage anywhere). PremiumLock had ZERO
// imports project-wide. BlurPreview was imported by PnLPage but never
// JSX-rendered (eslint-disabled no-unused-vars on the import line).
// PnLPage import cleaned up in same Item 7 patch.
// Per Rule L Phase 2: static + dynamic + JSX-instance scans all returned zero.
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// PaywallModal — FULL conversion paywall with FOMO + plans + bonus
// Used INTERNALLY by UpgradeNudge below (line 251 area).
// ─────────────────────────────────────────────────────────────────
// S-161: minPlan default was '₹29' — a hardcoded rupee shown to every country.
// Callers that pass minPlan still win; the fallback is now currency-neutral.
export function PaywallModal({ feature, minPlan = 'Pro', onClose, setPage, context = 'default' }) {
  const FOMO = {
    default:   { stat:'87% of traders using Premium are profitable',      icon:'📊' },
    signals:   { stat:'Pro traders get signals 3x faster than free users', icon:'⚡' },
    algo:      { stat:'Algo users made 4x more trades last week',          icon:'🤖' },
    heatmap:   { stat:'Heatmap users spotted 5 big moves this month',     icon:'🔥' },
    whale:     { stat:'Whale alerts caught 3 major pumps last week',       icon:'🐋' },
    limit:     { stat:'You just hit your limit — Pro users never stop',    icon:'⛔' },
    profit:    { stat:'Pro users grew portfolios 3x faster this month',    icon:'💰' },
  };
  const fomoData = FOMO[context] || FOMO.default;

  // ── S-161: PRICES AND LIMITS NOW COME FROM THE BACKEND, NOT FROM HERE ──────
  // This array used to hardcode price:'₹29'/'₹299'/'₹799'/'₹1499' with NO country
  // check — so every user in the US, Germany, Korea or Nigeria was quoted INDIAN
  // RUPEES on the paywall. It fires from AIAssistant.jsx on quota limits, so it
  // was not a rare path. It also hardcoded portfolio:'$25K'/'$350K'/'$650K'/'$1M',
  // which S-161's tier change (45K/630K/1.2M/1.8M) would have left advertising
  // the OLD numbers while the backend granted the new ones — the paywall lying.
  //
  // Split by responsibility: label/badge/colour are PAYWALL COPY and stay here.
  // price/portfolio are DATA and come from the one canonical fetcher.
  const { plans: apiPlans, loading: plansLoading } = usePlans();
  const PLAN_UI = [
    { id:'1d',  label:'3-Day Pass',   badge:null,          color:'#4B8BF5' },   // S-304: was '1 Day Trial' — the product is a 3-day pass
    { id:'1m',  label:'1 Month',      badge:'⭐ Popular',   color:G,         popular:true },
    { id:'3m',  label:'3 Months',     badge:'🔥 Best Deal', color:'#0ECB81' },
    { id:'12m', label:'1 Year',       badge:'👑 Max Value', color:'#FFD700' },
  ];
  const PLANS = PLAN_UI.map(ui => {
    const api = apiPlans.find(p => p.id === ui.id);
    return {
      ...ui,
      price:     api ? planPriceText(api)            : '',
      portfolio: api ? portfolioText(api.portfolio)  : '',
    };
  });
  // The 1d price for the main CTA — was a hardcoded '₹29' default prop.
  const trialPrice = PLANS.find(p => p.id === '1d')?.price || '';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:9500,
      display:'flex', alignItems:'flex-end', justifyContent:'center', padding:0 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:'linear-gradient(180deg,#1A1200,#0D0A00)', borderRadius:'20px 20px 0 0',
          width:'100%', maxWidth:480, maxHeight:'90vh', overflowY:'auto',
          border:`2px solid ${G}44`, borderBottom:'none', padding:'24px 20px 36px' }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:16 }}>
          <div style={{ fontSize:40, marginBottom:6 }}>⭐</div>
          <div style={{ fontWeight:900, color:G, fontSize:20, marginBottom:4 }}>
            Unlock {feature}
          </div>
          <div style={{ color:'#888', fontSize:13 }}>
            Join 100,000+ traders using PaperCrypto Pro
          </div>
        </div>

        {/* FOMO bar */}
        <div style={{ background:`${G}15`, border:`1px solid ${G}33`, borderRadius:10,
          padding:'10px 14px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:20 }}>{fomoData.icon}</span>
          <span style={{ color:G, fontSize:12, fontWeight:700, lineHeight:1.4 }}>{fomoData.stat}</span>
        </div>

        {/* Benefits */}
        <div style={{ marginBottom:16 }}>
          {[
            ['📈', 'Unlimited trades — never hit a limit again'],
            ['🎯', 'All AI signals, algo bots & advanced indicators'],
            ['🐋', 'Live whale tracking + advanced CVD analysis'],
            // S-048 (2026-05-10): Replaced 'Season rewards + advanced badges' line
            // with 'Priority customer support' per Sandeep's locked decision to
            // remove Season Rewards. Priority Support is a real 12m benefit
            // (implemented in S-055).
            ['🎫', 'Priority customer support (Pro)'],
            ['💎', '+10 Bonus Diamonds on first upgrade'],
            ['⚡', 'Pro users get signals 3x faster'],
          ].map(([icon, text], i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10,
              padding:'7px 0', borderBottom:'1px solid #1A1A1A' }}>
              <span style={{ fontSize:16 }}>{icon}</span>
              <span style={{ color:'#DDD', fontSize:12 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* Plans */}
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
          {PLANS.map(plan => (
            <button key={plan.id}
              onClick={() => {
                // S-017: Store selected plan ID so PremiumPage preloads it correctly
                // Was: just setPage('premium') — PremiumPage always defaulted to '1m' (Pro)
                // Now: PremiumPage reads pc_premium_preselect on mount and applies the right plan
                try { sessionStorage.setItem('pc_premium_preselect', plan.id); } catch {}
                onClose();
                setPage('premium');
              }}
              style={{ width:'100%', padding:'12px 16px', borderRadius:12,
                border:`2px solid ${plan.popular ? G : plan.color + '44'}`,
                background: plan.popular ? `linear-gradient(135deg,#1A0E00,#2D1800)` : 'rgba(255,255,255,0.04)',
                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between',
                boxShadow: plan.popular ? `0 0 15px ${G}22` : 'none' }}>
              <div style={{ textAlign:'left' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontWeight:800, color: plan.popular ? G : '#ddd', fontSize:14 }}>{plan.label}</span>
                  {plan.badge && (
                    <span style={{ background: plan.popular ? G : plan.color,
                      color: plan.popular ? '#000' : '#fff',
                      fontSize:9, fontWeight:900, padding:'2px 7px', borderRadius:20 }}>
                      {plan.badge}
                    </span>
                  )}
                </div>
                <div style={{ color:'#666', fontSize:11 }}>{plan.portfolio} portfolio</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight:900, color:plan.color, fontSize:17 }}>{plan.price}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Diamond bonus */}
        <div style={{ background:'#B26BFF12', border:'1px solid #B26BFF33', borderRadius:10,
          padding:'10px 14px', marginBottom:16, textAlign:'center',
          fontSize:12, color:'#B26BFF', fontWeight:700 }}>
          🎁 Bonus: +10 💎 Diamonds free with first upgrade
        </div>

        {/* Main CTA */}
        <button onClick={() => {
          // S-017: "Start with ₹29 Trial" CTA must explicitly preload the 1-day trial plan
          // Was: setPage('premium') only — PremiumPage defaulted to '1m' (Pro) ignoring user intent
          // Now: stores '1d' so PremiumPage preselects the trial card
          try { sessionStorage.setItem('pc_premium_preselect', '1d'); } catch {}
          onClose();
          setPage('premium');
        }}
          style={{ width:'100%', padding:'15px', borderRadius:14, border:'none',
            background:`linear-gradient(135deg,${GD},${G})`,
            color:'#000', fontWeight:900, fontSize:16, cursor:'pointer',
            boxShadow:`0 4px 20px ${G}44`, marginBottom:10 }}>
          {plansLoading ? '⭐ Start Trial →' : `⭐ Start with ${trialPrice} Trial →`}
        </button>
        <button onClick={onClose}
          style={{ width:'100%', padding:'9px', borderRadius:10, border:'1px solid #333',
            background:'transparent', color:'#555', fontSize:13, cursor:'pointer' }}>
          Maybe later
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// 4. UpgradeNudge — inline contextual nudge strip
// Usage: <UpgradeNudge context="signals" />
// ─────────────────────────────────────────────────────────────────
export function UpgradeNudge({ context = 'default', dismissable = true, compact = false }) {
  const { setPage } = useApp();
  const [dismissed, setDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // v275 (G4.23a — Doubt #81b/#83b): Use canonical usePremium hook
  const { isPremium } = usePremium();

  if (isPremium || dismissed) return null;

  const NUDGES = {
    default:     { msg: '⭐ Upgrade for unlimited access — from ₹29',                icon:'⭐', cta:'Upgrade' },
    signals:     { msg: '⚡ You missed 3 signals today — Pro gets them all',          icon:'🎯', cta:'Get Signals' },
    profit:      { msg: '💰 87% of Pro traders are profitable this week',             icon:'📈', cta:'Go Pro' },
    limit:       { msg: '⛔ Limit reached — Pro users never stop trading',             icon:'⛔', cta:'Remove Limit' },
    algo:        { msg: '🤖 Algo users made 4x more trades — unlock all 6 bots',     icon:'🤖', cta:'Unlock Bots' },
    heatmap:     { msg: '🔥 Heatmap spotted 5 big moves this month — you missed them', icon:'🔥', cta:'Unlock' },
    // v235: tournament nudge removed (B+ plan)
    store:       { msg: '💎 Low on Currency? Get Diamonds to convert — ₹29 gets 50💎', icon:'💎', cta:'Get Diamonds' },
  };
  const n = NUDGES[context] || NUDGES.default;

  if (compact) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px',
        background:`${G}10`, border:`1px solid ${G}22`, borderRadius:20,
        fontSize:12, cursor:'pointer' }} onClick={() => setShowModal(true)}>
        <span>{n.icon}</span>
        <span style={{ color:G, fontWeight:700 }}>{n.cta}</span>
        {showModal && <PaywallModal context={context} onClose={() => setShowModal(false)} setPage={setPage}/>}
      </div>
    );
  }

  return (
    <div style={{ background:`linear-gradient(135deg,#1A0E00,#2D1800)`,
      border:`1px solid ${G}44`, borderRadius:12,
      padding:'12px 14px', marginBottom:12,
      display:'flex', alignItems:'center', gap:10 }}>
      <span style={{ fontSize:18 }}>{n.icon}</span>
      <div style={{ flex:1 }}>
        <div style={{ color:G, fontSize:12, fontWeight:700 }}>{n.msg}</div>
      </div>
      <button onClick={() => setShowModal(true)}
        style={{ flexShrink:0, padding:'6px 14px', borderRadius:20, border:'none',
          background:`linear-gradient(135deg,${GD},${G})`, color:'#000',
          fontWeight:800, fontSize:11, cursor:'pointer', whiteSpace:'nowrap' }}>
        {n.cta}
      </button>
      {dismissable && (
        <span onClick={() => setDismissed(true)}
          style={{ color:'#555', fontSize:16, cursor:'pointer', marginLeft:4 }}>✕</span>
      )}
      {showModal && <PaywallModal context={context} onClose={() => setShowModal(false)} setPage={setPage}/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// LimitProgress + UpgradeModal + LimitBadge + default export REMOVED v275 (Item 7)
// All four had ZERO consumers verified via Rule L Phase 2 deep scan
// (static imports, dynamic imports, JSX usage all returned zero).
//
// • LimitProgress — was a visual progress bar, never JSX-rendered anywhere
// • UpgradeModal  — was a one-line legacy compat layer wrapping PaywallModal
// • LimitBadge    — was a one-line legacy compat layer wrapping LimitProgress
// • default export (React.memo(PremiumLock)) — PremiumLock itself was dead
//
// Per Rule K research (Pensero 2026): paper-trail doc comment preserved for
// git archaeology. Per Rule J Q4 future-state: if any of these components are
// resurrected later, they should be rebuilt from scratch using the canonical
// usePremium hook directly (these legacy wrappers offered no real abstraction).
// ─────────────────────────────────────────────────────────────────
